import { Fragment } from 'react';

import {
	Button,
	Checkbox,
	FormControl,
	FormControlLabel,
	FormGroup,
	FormHelperText,
	FormLabel,
	InputLabel,
	MenuItem,
	Select,
	Slider,
	TextField,
	Typography,
} from '@mui/material';

import { strToSlug } from '@/utils';
import MultiSelect from '@/components/DynamicForm/FormElements/ui-kits/default/MultiSelect/MultiSelect';
import ImagePickerWithPreview from '@/components/DynamicForm/FormElements/ui-kits/default/ImagePickerWithPreview/ImagePickerWithPreview';
import RadioGroup from '@/components/DynamicForm/FormElements/ui-kits/default/RadioGroup/RadioGroup';
import {
	CollectionInput,
	DateTextInputElement,
	Message,
	NumberInput,
	RangeInput,
	ReviewCard,
	SimpleInput,
	UserInteractionEl,
	ImagePickerElement,
	MarkdownTextType,
} from '@/components/DynamicForm/types/DynamicFormTypes';
import { cssprefix, flattenIntoKey } from '@/components/DynamicForm/utils/utils';
import MarkdownText from '@/components/DynamicForm/FormElements/ui-kits/default/MarkdownText/MarkdownText';

import DateTextInput from './DateTextInput/DateTextInput';
import { dynamicFormCheckboxStyles } from './FromElements.styles';

const typeSafeNoOp = (_: number) => {
	return void 0;
};

export default {
	select: ({
		name,
		label,
		options = [],
		value,
		onChange,
		onBlur,
		placeholder,
		showError,
		errMsg,
	}: CollectionInput) => (
		<>
			<InputLabel data-test-id={`${name}-label`} htmlFor={name}>
				{label}
			</InputLabel>
			<FormControl error={showError}>
				<Select
					name={name}
					id={name}
					value={value as string}
					onChange={(e) => onChange?.(e)}
					onBlur={onBlur}
					displayEmpty
					renderValue={(value) => options.find((item) => item.value === value)?.label ?? placeholder}
				>
					{options.map(({ label: optLabel, value: optValue }, index) => (
						<MenuItem key={`${optValue}_${index}`} value={optValue}>
							<Typography variant="bodyMediumBook">{optLabel}</Typography>
						</MenuItem>
					))}
				</Select>
				{errMsg && showError && <FormHelperText>{errMsg}</FormHelperText>}
			</FormControl>
		</>
	),

	checkbox: ({ name, label, value, onChange, onBlur, showError, errMsg }: SimpleInput) => (
		<FormControl data-test-id={`${name}-checkbox`} error={showError}>
			<FormGroup>
				<FormControlLabel
					name={name}
					value={value}
					label={<MarkdownText variant="bodyMediumBook" text={label} />}
					onChange={(e) => onChange?.(e)}
					onBlur={onBlur}
					control={
						<Checkbox
							className={`${cssprefix}checkbox`}
							checked={!!value}
							sx={dynamicFormCheckboxStyles.checkboxStyles}
						/>
					}
					sx={dynamicFormCheckboxStyles.formControlLabelStyles}
				/>
			</FormGroup>
			{errMsg && showError && <FormHelperText>{errMsg}</FormHelperText>}
		</FormControl>
	),

	'multi-select': (props: CollectionInput) => <MultiSelect {...props} />,
	radio: (props: CollectionInput) => <RadioGroup {...props} />,

	//Hold-off
	textarea: ({ name, label, placeholder, value, onChange, onBlur, showError, errMsg, labelSpacing }: SimpleInput) => (
		<>
			{(label || labelSpacing) && (
				<InputLabel
					className={`${cssprefix}label-spacer-${labelSpacing && !label ? labelSpacing : 'nn'}`}
					data-test-id={`${name}-textarea`}
					htmlFor={name}
				>
					{label || ' '}
				</InputLabel>
			)}
			<TextField
				multiline
				rows={2}
				name={name}
				id={name}
				placeholder={placeholder}
				onChange={onChange}
				onBlur={onBlur}
				value={(value || '') as string}
				error={showError}
				helperText={<Typography variant="errorValidation">{errMsg}</Typography>}
			/>
		</>
	),

	//Hold-off
	message: ({ title, copy, tagData }: Message) => {
		const titleID = title && title?.length > 0 ? `-${strToSlug(title.join('').slice(0, 15))}-title` : '';
		const copyID = copy && copy?.length > 0 ? `-${strToSlug(copy.join('').slice(0, 15))}-copy` : '';

		return (
			<div
				data-test-id={`message${titleID}${copyID}`}
				{...flattenIntoKey(tagData, 'data')}
				className={`${cssprefix}message`}
			>
				{title?.length && (
					<Typography variant="heroMedium" className={`${cssprefix}message-title`} sx={{ mb: '1rem' }}>
						{title.map((text, idx) => (
							<span key={`title_${text.slice(0, 7)}_${idx}`}> {text} </span>
						))}
					</Typography>
				)}
				{copy?.length && (
					<Typography variant="bodyMediumBook" className={`${cssprefix}message-copy`}>
						<MarkdownText text={copy.join('\n\n')} />
					</Typography>
				)}
			</div>
		);
	},

	markdownText: (props: MarkdownTextType) => <MarkdownText {...props} />,

	//Hold-off
	reviewCard: ({ label = '', value, keyList, stepIdx, tagData, skipTo = typeSafeNoOp }: ReviewCard) => (
		<div
			data-test-id={`${label}-review-card`}
			{...flattenIntoKey(tagData, 'data')}
			className={`${cssprefix}review-card`}
		>
			<h3>{label}</h3>
			<button
				onClick={(e) => {
					e.preventDefault();
					skipTo(stepIdx);
				}}
			>
				Edit
			</button>
			<dl>
				{keyList.map((keyName, idx) => (
					<Fragment key={`${keyName}_${idx}`}>
						<dt>{keyName}</dt>
						<dd>{JSON.stringify(value?.[keyName])}</dd>
					</Fragment>
				))}
			</dl>
		</div>
	),

	userInteraction: ({
		label,
		midStepCall,
		userInteractionNum,
		disabled,
		copy,
		redirect,
		variant = 'primaryDefault',
		size = 'M',
		tagData,
	}: UserInteractionEl) => (
		<Button
			className={`${cssprefix}user-interaction`}
			variant={variant}
			size={size}
			data-test-id={`${strToSlug(label)}-user-interaction`}
			{...flattenIntoKey(tagData, 'data')}
			title={label}
			disabled={disabled}
			onClick={(e) => {
				e.preventDefault();
				if (redirect) {
					window.location.href = redirect;
					return;
				}
				if (typeof userInteractionNum === 'number') {
					midStepCall?.(userInteractionNum);
				}
			}}
		>
			{copy}
		</Button>
	),

	//Hold-off
	range: ({
		name,
		label,
		placeholder,
		value,
		onChange,
		onBlur,
		step = 1,
		min = 0,
		max = 10,
		unit = '',
		defaultValue,
		tagData,
	}: RangeInput) => (
		<>
			<FormLabel htmlFor={name}>
				{label} ({(value || defaultValue) as string} {unit})
			</FormLabel>
			<Slider
				data-test-id={`${name}-range`}
				{...flattenIntoKey(tagData, 'data')}
				name={name}
				id={name}
				placeholder={placeholder}
				min={min}
				max={max}
				step={step}
				onChange={(e) => onChange?.(e)}
				onBlur={onBlur}
				value={(value || defaultValue) as number}
			/>
		</>
	),

	number: ({
		name,
		label,
		placeholder,
		value,
		onChange,
		onBlur,
		showError,
		errMsg,
		labelSpacing,
		tagData,
	}: NumberInput) => (
		<>
			{(label || labelSpacing) && (
				<InputLabel
					className={`${cssprefix}label-spacer-${labelSpacing && !label ? labelSpacing : 'nn'}`}
					data-test-id={`${name}-number`}
					htmlFor={name}
				>
					{label || ' '}
				</InputLabel>
			)}
			<TextField
				label={placeholder}
				variant="outlined"
				type="number"
				name={name}
				id={name}
				onChange={onChange}
				onBlur={onBlur}
				helperText={<Typography variant="errorValidation">{errMsg}</Typography>}
				error={showError}
				value={value as number}
				inputProps={flattenIntoKey(tagData, 'data')}
			/>
		</>
	),

	// Need to revisit validation for image picker
	image: ({
		name,
		uploadInfo,
		reviewInfo,
		fileInfo,
		accept,
		centerAlignInfo,
		showError,
		errMsg,
		onChange,
		onBlur,
		value,
	}: ImagePickerElement) => (
		<FormControl>
			<ImagePickerWithPreview
				data-test-id={`${name}-image-picker`}
				id={name}
				accept={accept}
				centerAlignInfo={centerAlignInfo}
				fileInfo={fileInfo}
				reviewInfo={reviewInfo}
				uploadInfo={uploadInfo}
				selectedImageFile={value as File}
				onChange={onChange}
				onBlur={onBlur}
				showError={showError}
			/>
			{errMsg && showError && (
				<FormHelperText>
					<Typography variant="errorValidation">{errMsg}</Typography>
				</FormHelperText>
			)}
		</FormControl>
	),

	'date-text-input': (props: DateTextInputElement) => <DateTextInput {...props} />,

	// Handle other HTML5 input types like date, email, etc.
	default: ({
		name,
		label,
		placeholder,
		type,
		value,
		onChange,
		onBlur,
		showError,
		errMsg,
		labelSpacing,
		tagData,
	}: SimpleInput) => (
		<>
			{(label || labelSpacing) && (
				<InputLabel
					className={`${cssprefix}label-spacer-${labelSpacing && !label ? labelSpacing : 'nn'}`}
					data-test-id={`${name}-default`}
					htmlFor={name}
				>
					{label || ' '}
				</InputLabel>
			)}
			<TextField
				variant="outlined"
				type={type}
				name={name}
				id={name}
				placeholder={placeholder}
				onChange={onChange}
				onBlur={onBlur}
				value={value}
				error={showError}
				helperText={<Typography variant="errorValidation">{errMsg}</Typography>}
				inputProps={flattenIntoKey(tagData, 'data')}
			/>
		</>
	),
};
